import { useFormContext, Controller } from 'react-hook-form';
import type {
  InfoFieldOption,
  RecentEpoccField,
  RecentEpoccFields,
} from '../../../lib';
import CareMapprDropdown from '../Dropdown/CareMapprDropdown';
import { getTodayDateString, YYYY_MM_DD } from '../../helpers';

interface MostRecentEPOCCProps {
  selectedField: 'adult' | 'paed';
  onUpdateSelectedField: (type: 'adult' | 'paed') => void;
  fields: RecentEpoccFields;
}

const MostRecentEPOCC = ({
  selectedField,
  onUpdateSelectedField,
  fields,
}: MostRecentEPOCCProps) => {
  const {
    control,
    resetField,
    formState: { errors },
  } = useFormContext();

  const keyFields: InfoFieldOption[] = fields
    ? Object.keys(fields)
        ?.map((key) => ({
          label: key === 'adult' ? 'Adult' : 'Paed',
          value: key,
        }))
        ?.sort((a, b) => a.label.localeCompare(b.label))
    : [];

  const selectedFields: RecentEpoccField[] =
    selectedField === 'adult' ? [...fields.adult] : [...fields.paed];

  const handleFieldChange = (type: 'adult' | 'paed') => {
    onUpdateSelectedField(type);
    const otherFields = type === 'adult' ? fields.paed : fields.adult;
    otherFields.forEach((field) => resetField(field.id, { defaultValue: '' }));
  };

  return (
    <div className="container-fluid">
      <div className="font-14 font-weight-600 mt-2 mb-4">Most recent EPOCC</div>

      <div className="my-2">
        {keyFields.map((opt) => (
          <div key={opt.value} className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              id={`${opt.label}-${opt.value}`}
              value={opt.value}
              checked={selectedField === opt.value}
              onChange={(e) => {
                handleFieldChange(opt.value as 'adult' | 'paed');
              }}
            />
            <label
              className="form-check-label"
              htmlFor={`${opt.label}-${opt.value}`}
            >
              {opt.label}
            </label>
          </div>
        ))}
      </div>

      <div className="row">
        {selectedFields.map((field) => {
          const fieldError = errors[field.id];
          const isInvalid = !!fieldError;

          return (
            <div key={field.id} className="col-12 col-md-4 mb-3">
              <label className="mb-2 labelGrey font-14">
                {field.label}
                {field.required ? ' *' : ''}
              </label>

              <Controller
                name={field.id}
                control={control}
                rules={{ required: field.required }}
                render={({ field: controllerField }) => {
                  const commonClass = `form-control ${isInvalid ? 'is-invalid' : ''}`;

                  // RADIO FIELD
                  if (field.type === 'radio' && field.options) {
                    return (
                      <div>
                        {field.options.map((opt) => (
                          <div
                            key={opt.value}
                            className="form-check form-check-inline"
                          >
                            <input
                              className={`form-check-input ${isInvalid ? 'is-invalid' : ''}`}
                              type="radio"
                              id={`${field.id}-${opt.value}`}
                              value={opt.value}
                              checked={controllerField.value === opt.value}
                              onChange={() =>
                                controllerField.onChange(opt.value)
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`${field.id}-${opt.value}`}
                            >
                              {opt.label}
                            </label>
                          </div>
                        ))}
                        {isInvalid && (
                          <div className="invalid-feedback d-block">
                            This field is required.
                          </div>
                        )}
                      </div>
                    );
                  }

                  // SELECT FIELD
                  if (field.type === 'select' && field.options) {
                    const options = field.options;
                    return (
                      <>
                        <CareMapprDropdown
                          options={options}
                          value={
                            options.find(
                              (opt) => opt.value === controllerField.value,
                            ) || null
                          }
                          onChange={(selected) =>
                            controllerField.onChange(selected?.value ?? '')
                          }
                          hasError={isInvalid}
                          name={field.id}
                          controlId={field.id}
                          disabled={false}
                          placeholder={`Select ${field.label.toLocaleLowerCase()}`}
                        />
                        {isInvalid && (
                          <div className="invalid-feedback">
                            This field is required.
                          </div>
                        )}
                      </>
                    );
                  }

                  // TEXT / NUMBER / ETC
                  return (
                    <>
                      <input
                        type={field.type}
                        {...controllerField}
                        className={commonClass}
                        max={
                          field.type === 'date'
                            ? getTodayDateString(YYYY_MM_DD)
                            : undefined
                        }
                      />
                      {isInvalid && (
                        <div className="invalid-feedback">
                          This field is required.
                        </div>
                      )}
                    </>
                  );
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MostRecentEPOCC;
